import React, {useCallback, useState} from 'react'
import PropTypes from 'prop-types'
import {
  Col,
  CustomerDialogButtonRow,
  H3,
  InputContainer,
  Row,
  TextAreaPure,
  WarningAlert,
} from '@modifi/ui'
import {Form, FormActionsContext, FormInput, SubmitBehavior} from '@modifi/plugin-forms'
import {useTranslation} from '@modifi/plugin-i18n'
import {InferPropTypes} from '@modifi/typescript-utils'
import ms from 'ms'
import {useAbility} from '../../../../constants/permissions'
import {TRANSACTION} from '../../../../constants/section'
import {Description} from './styles'
import {parseClipboard} from './utils'

const TextAreaWithContainer = (props: any) => (
  <InputContainer inputComponent={TextAreaPure} {...props} />
)

const propTypes = {
  params: PropTypes.shape({
    isPassThrough: PropTypes.bool.isRequired,
    id: PropTypes.number.isRequired,
  }).isRequired,
  handleClose: PropTypes.func.isRequired,
  search: PropTypes.func.isRequired,
  fetchWithFilterParams: PropTypes.func.isRequired,
  setMatchingList: PropTypes.func.isRequired,
  addSnackBar: PropTypes.func.isRequired,
  clearMatchList: PropTypes.func.isRequired,
}

const defaultProps = {}

export type BulkMatchingDialogProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const BulkMatchingDialog: React.FC<BulkMatchingDialogProps> = ({
  handleClose,
  search,
  fetchWithFilterParams,
  setMatchingList,
  addSnackBar,
  clearMatchList,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const {t} = useTranslation('moneta')
  const ability = useAbility()
  const isAllowed = ability.can('create', TRANSACTION)

  const flatten = useCallback((grid: string[][]) => grid.flat(), [])

  const handleSubmit = useCallback(
    async (changes: any) => {
      // clear previously set list
      clearMatchList()
      setIsLoading(true)
      try {
        const {orderReference, amount} = changes
        const orderReferences = flatten(parseClipboard(orderReference))

        // capture each search result or failure
        const settles = await Promise.allSettled(
          orderReferences.map(async ref => {
            const results = await search(ref)
            // if multiple results fetched we consider it as a mismatch
            if (results.length === 1 && results[0].externalId) {
              return results[0].externalId
            }
            throw new Error(`Expected 1 match for "${ref}", got ${results.length}`)
          })
        )

        const externalIds: string[] = []
        const failedRefs: string[] = []

        settles.forEach((res, i) => {
          if (res.status === 'fulfilled' && res.value) {
            externalIds.push(res.value)
          } else {
            failedRefs.push(orderReferences[i])
          }
        })

        // if *all* failed
        if (externalIds.length === 0) {
          addSnackBar({type: 'alert', message: t('bulk-matching.error.message')})
          return false
        }

        const flatNums: number[] = amount
          ? flatten(parseClipboard(amount)).map(cell => parseFloat(cell))
          : []

        const useAmounts = flatNums.length === externalIds.length && flatNums.length > 0

        await fetchWithFilterParams({
          'filter.externalId.order.orderReferenceCode': externalIds,
        })

        const matchingList = externalIds.map((id, idx) => ({
          externalId: id,
          amount: useAmounts ? flatNums[idx] : 0,
        }))
        setMatchingList(matchingList)

        // if *some* failed
        if (failedRefs.length > 0) {
          return addSnackBar({
            type: 'warn',
            message: t('bulk-matching.partial-error', {refs: failedRefs.join(', ')}),
            hideDismissButton: false,
            duration: ms('60s'),
          })
        }
        return addSnackBar({
          type: useAmounts ? 'success' : 'warn',
          message: t(`bulk-matching.${useAmounts ? 'success' : 'info'}.message`),
          hideDismissButton: false,
          duration: ms('60s'),
        })
      } catch (err) {
        addSnackBar({type: 'alert', message: t('bulk-matching.error.message')})
        return false
      } finally {
        setIsLoading(false)
        handleClose()
      }
    },
    [
      clearMatchList,
      flatten,
      fetchWithFilterParams,
      setMatchingList,
      addSnackBar,
      t,
      search,
      handleClose,
    ]
  )

  const maxLengthMessage = useCallback(
    ({maxLength}: {maxLength: number}) => t('bulk-matching.dialog.max-length.warning', {maxLength}),
    [t]
  )

  return (
    <>
      {!isAllowed && (
        <Row mt={30} mb={10}>
          <Col col={10} offset={1}>
            <WarningAlert>{t('warning.alert.permission-denied')}</WarningAlert>
          </Col>
        </Row>
      )}
      <Row justifyContent="center">
        <Col auto>
          <H3 mt={40}>{t('bulk-matching.dialog.header')}</H3>
        </Col>
      </Row>

      <Row my={24}>
        <Col col={10} offset={1}>
          <Description>{t('bulk-matching.dialog.sub-header')}</Description>
        </Col>
      </Row>
      <Form submitBehavior={SubmitBehavior.SUBMIT_DISABLED_UNLESS_VALID} onSubmit={handleSubmit}>
        <Row>
          <Col col={8} offset={2}>
            <FormInput
              name="orderReference"
              label={t('bulk-matching.dialog.order-reference.label')}
              inputComponent={TextAreaWithContainer}
              rows={5}
              maxLength={500}
              maxLengthMessage={maxLengthMessage}
            />
          </Col>
        </Row>
        <Row>
          <Col col={8} offset={2}>
            <FormInput
              name="amount"
              label={t('bulk-matching.dialog.amount.label')}
              inputComponent={TextAreaWithContainer}
              rows={5}
              maxLength={500}
              maxLengthMessage={maxLengthMessage}
            />
          </Col>
        </Row>
        <Row>
          <Col col={8} offset={2}>
            <FormActionsContext.Consumer>
              {ctx => (
                <CustomerDialogButtonRow
                  canSubmit={ctx.hasChanges && !ctx.validationPending && isAllowed}
                  onSubmitClick={ctx.submit}
                  submitText={t('bulk-matching.dialog.submit.cta')}
                  hasCancel
                  cancelText={t('bulk-matching.dialog.cancel.cta')}
                  onCancelClick={handleClose}
                  loading={isLoading}
                  leftSpacing={0}
                  rightSpacing={0}
                />
              )}
            </FormActionsContext.Consumer>
          </Col>
        </Row>
      </Form>
    </>
  )
}

BulkMatchingDialog.propTypes = propTypes
BulkMatchingDialog.defaultProps = defaultProps

export default BulkMatchingDialog
